/**
 * VenueOS Analytics Client
 *
 * Provides analytics capabilities for first-party VenueOS applications.
 * This is an internal SDK - NOT published to npm.
 */
import { isAnalyticsMessage, isAnalyticsTokenResponseMessage, createTokenRequestMessage, } from './messages';
/**
 * Analytics Client - provides access to Snowflake analytics data
 *
 * This package is internal-only and not published to npm.
 * Only first-party apps can use this SDK.
 *
 * @example
 * ```typescript
 * import { VenueOS } from '@mappedin/venueos-applet';
 * import { AnalyticsClient } from '@venueos/analytics-sdk';
 *
 * const venueos = await VenueOS.init();
 * const analytics = new AnalyticsClient(venueos);
 *
 * const events = await analytics.getEvents({
 *   venues: ['mall-1'],
 *   actions: ['search'],
 *   startDate: '2025-01-01',
 *   endDate: '2025-01-31'
 * });
 * ```
 */
export class AnalyticsClient {
    _baseClient;
    _pendingTokenRequests;
    _requestIdCounter = 0;
    _messageHandler = null;
    constructor(baseClient) {
        this._baseClient = baseClient;
        this._pendingTokenRequests = new Map();
        this._setupMessageListener();
    }
    /**
     * Get the underlying VenueOS client
     */
    get baseClient() {
        return this._baseClient;
    }
    /**
     * Get a Snowflake JWT token for direct connection
     *
     * This token can be used with the snowflake-sdk to connect directly
     * to Snowflake for complex queries.
     *
     * @param options - Optional timeout configuration
     * @returns Snowflake credentials including JWT token
     *
     * @example
     * ```typescript
     * const credentials = await analytics.getSnowflakeToken();
     *
     * // Use with snowflake-sdk
     * const connection = snowflake.createConnection({
     *   account: credentials.accountUrl,
     *   authenticator: 'oauth',
     *   token: credentials.token,
     *   warehouse: credentials.warehouse,
     *   role: credentials.role,
     * });
     * ```
     */
    async getSnowflakeToken(options = {}) {
        const { timeout = 30000 } = options;
        const requestId = `analytics-token-${++this._requestIdCounter}-${Date.now()}`;
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                this._pendingTokenRequests.delete(requestId);
                reject(new Error(`[VenueOS Analytics] Token request timeout (${timeout}ms)`));
            }, timeout);
            this._pendingTokenRequests.set(requestId, {
                resolve: (credentials) => {
                    clearTimeout(timeoutId);
                    this._pendingTokenRequests.delete(requestId);
                    resolve(credentials);
                },
                reject: (error) => {
                    clearTimeout(timeoutId);
                    this._pendingTokenRequests.delete(requestId);
                    reject(error);
                },
            });
            const request = createTokenRequestMessage(requestId);
            window.parent.postMessage(request, '*');
        });
    }
    /**
     * Execute a raw SQL query against Snowflake
     *
     * The query is validated server-side to ensure:
     * - Only SELECT statements allowed (no DDL/DML)
     * - Only allowed tables can be queried (session_events, kiosks_view)
     * - Results are filtered by the user's organization
     *
     * @param sql - SQL query string (use ? for parameters)
     * @param params - Query parameters (optional)
     * @returns Query results
     *
     * @example
     * ```typescript
     * const result = await analytics.query(
     *   'SELECT ACTION, COUNT(*) as cnt FROM session_events WHERE VENUE = ? GROUP BY ACTION ORDER BY cnt DESC LIMIT 10',
     *   ['mall-1']
     * );
     * console.log(result.data);
     * ```
     */
    async query(sql, params = []) {
        const response = await this._baseClient.api.fetch('/api/analytics/query', {
            method: 'POST',
            body: { sql, params },
        });
        if (!response.ok) {
            const errorData = response.data;
            throw new Error(`[VenueOS Analytics] Query failed: ${errorData?.message || errorData?.error || response.status}`);
        }
        return {
            data: response.data.data,
            columns: response.data.columns,
            rowCount: response.data.rowCount,
        };
    }
    /**
     * Get analytics events from session_events table
     *
     * @param options - Query options
     * @returns Events matching the query
     */
    async getEvents(options = {}) {
        const params = new URLSearchParams();
        if (options.venues?.length)
            params.set('venues', options.venues.join(','));
        if (options.actions?.length)
            params.set('actions', options.actions.join(','));
        if (options.startDate)
            params.set('startDate', options.startDate);
        if (options.endDate)
            params.set('endDate', options.endDate);
        if (options.contexts?.length)
            params.set('contexts', options.contexts.join(','));
        if (options.platforms?.length)
            params.set('platforms', options.platforms.join(','));
        if (options.limit !== undefined)
            params.set('limit', options.limit.toString());
        if (options.offset !== undefined)
            params.set('offset', options.offset.toString());
        if (options.orderBy)
            params.set('orderBy', options.orderBy);
        if (options.orderDirection)
            params.set('orderDirection', options.orderDirection);
        const queryString = params.toString();
        const path = `/api/analytics/events${queryString ? `?${queryString}` : ''}`;
        const response = await this._baseClient.api.fetch(path);
        if (!response.ok) {
            const errorData = response.data;
            throw new Error(`[VenueOS Analytics] Failed to fetch events: ${errorData?.message || errorData?.error || response.status}`);
        }
        // Transform snake_case to camelCase
        const events = response.data.data.map((e) => ({
            id: e.id,
            timestamp: e.timestamp,
            timestampWall: e.timestamp_wall,
            action: e.action,
            venue: e.venue,
            venueName: e.venue_name,
            organization: e.organization,
            organizationName: e.organization_name,
            sessionId: e.session_id,
            deviceId: e.device_id,
            context: e.context,
            platform: e.platform,
            language: e.language,
            version: e.version,
            data: e.data,
        }));
        return {
            data: events,
            total: response.data.total,
            hasMore: response.data.hasMore,
        };
    }
    /**
     * Get session statistics
     *
     * @param options - Query options
     * @returns Session statistics
     */
    async getSessions(options = {}) {
        const params = new URLSearchParams();
        if (options.venues?.length)
            params.set('venues', options.venues.join(','));
        if (options.startDate)
            params.set('startDate', options.startDate);
        if (options.endDate)
            params.set('endDate', options.endDate);
        if (options.groupBy)
            params.set('groupBy', options.groupBy);
        const queryString = params.toString();
        const path = `/api/analytics/sessions${queryString ? `?${queryString}` : ''}`;
        const response = await this._baseClient.api.fetch(path);
        if (!response.ok) {
            const errorData = response.data;
            throw new Error(`[VenueOS Analytics] Failed to fetch sessions: ${errorData?.message || errorData?.error || response.status}`);
        }
        const stats = response.data.data.map((s) => ({
            groupKey: s.group_key,
            sessionCount: s.session_count,
            eventCount: s.event_count,
            avgEventsPerSession: s.avg_events_per_session,
            uniqueDevices: s.unique_devices,
        }));
        return {
            data: stats,
            total: response.data.total,
        };
    }
    /**
     * Get kiosk/directory information
     *
     * @param options - Query options
     * @returns Kiosk information
     */
    async getKiosks(options = {}) {
        const params = new URLSearchParams();
        if (options.venues?.length)
            params.set('venues', options.venues.join(','));
        const queryString = params.toString();
        const path = `/api/analytics/kiosks${queryString ? `?${queryString}` : ''}`;
        const response = await this._baseClient.api.fetch(path);
        if (!response.ok) {
            const errorData = response.data;
            throw new Error(`[VenueOS Analytics] Failed to fetch kiosks: ${errorData?.message || errorData?.error || response.status}`);
        }
        const kiosks = response.data.data.map((k) => ({
            kioskId: k.kiosk_id,
            kioskName: k.kiosk_name,
            venue: k.venue,
            venueName: k.venue_name,
            organization: k.organization,
        }));
        return {
            data: kiosks,
        };
    }
    /**
     * Get analytics summary
     *
     * @param options - Query options
     * @returns Analytics summary with aggregated metrics
     */
    async getSummary(options = {}) {
        const params = new URLSearchParams();
        if (options.venues?.length)
            params.set('venues', options.venues.join(','));
        if (options.startDate)
            params.set('startDate', options.startDate);
        if (options.endDate)
            params.set('endDate', options.endDate);
        const queryString = params.toString();
        const path = `/api/analytics/summary${queryString ? `?${queryString}` : ''}`;
        const response = await this._baseClient.api.fetch(path);
        if (!response.ok) {
            const errorData = response.data;
            throw new Error(`[VenueOS Analytics] Failed to fetch summary: ${errorData?.message || errorData?.error || response.status}`);
        }
        return {
            totalSessions: response.data.totalSessions,
            totalEvents: response.data.totalEvents,
            topActions: response.data.topActions,
            topVenues: response.data.topVenues.map((v) => ({
                venue: v.venue,
                venueName: v.venue_name,
                eventCount: v.event_count,
                sessionCount: v.session_count,
            })),
            eventsByPlatform: response.data.eventsByPlatform,
            eventsByContext: response.data.eventsByContext,
        };
    }
    /**
     * Get venue-level statistics
     *
     * @param options - Query options
     * @returns Venue statistics
     */
    async getVenueStats(options = {}) {
        const params = new URLSearchParams();
        if (options.venueId)
            params.set('venueId', options.venueId);
        if (options.venueIds?.length)
            params.set('venueIds', options.venueIds.join(','));
        if (options.days !== undefined)
            params.set('days', options.days.toString());
        const queryString = params.toString();
        const path = `/api/analytics/venue-stats${queryString ? `?${queryString}` : ''}`;
        const response = await this._baseClient.api.fetch(path);
        if (!response.ok) {
            const errorData = response.data;
            throw new Error(`[VenueOS Analytics] Failed to fetch venue stats: ${errorData?.message || errorData?.error || response.status}`);
        }
        const stats = response.data.data.map((s) => ({
            venue: s.venue,
            venueName: s.venue_name,
            sessionCount: s.session_count,
            eventCount: s.event_count,
            uniqueDevices: s.unique_devices,
            avgEventsPerSession: s.avg_events_per_session,
            firstEvent: s.first_event,
            lastEvent: s.last_event,
        }));
        return {
            success: true,
            data: stats,
        };
    }
    /**
     * Get time series analytics data
     *
     * @param options - Query options
     * @returns Time series data points
     */
    async getTimeSeries(options = {}) {
        const params = new URLSearchParams();
        if (options.days !== undefined)
            params.set('days', options.days.toString());
        if (options.interval)
            params.set('interval', options.interval);
        if (options.venueIds?.length)
            params.set('venueIds', options.venueIds.join(','));
        const queryString = params.toString();
        const path = `/api/analytics/time-series${queryString ? `?${queryString}` : ''}`;
        const response = await this._baseClient.api.fetch(path);
        if (!response.ok) {
            const errorData = response.data;
            throw new Error(`[VenueOS Analytics] Failed to fetch time series: ${errorData?.message || errorData?.error || response.status}`);
        }
        const points = response.data.data.map((p) => ({
            time: p.time,
            sessionCount: p.session_count,
            eventCount: p.event_count,
            uniqueDevices: p.unique_devices,
            venueCount: p.venue_count,
        }));
        return {
            success: true,
            data: points,
        };
    }
    /**
     * Pull data from a Snowflake table
     *
     * @param options - Query options including table name
     * @returns Table data
     */
    async pullTableData(options) {
        if (!options.table) {
            throw new Error('[VenueOS Analytics] Table name is required');
        }
        // Use POST to avoid URL length limits with large venueIds
        const response = await this._baseClient.api.fetch('/api/analytics/table', {
            method: 'POST',
            body: {
                table: options.table,
                schema: options.schema,
                database: options.database,
                limit: options.limit,
                offset: options.offset,
                where: options.where,
                orderBy: options.orderBy,
                orderDirection: options.orderDirection,
                timeFilter: options.timeFilter,
                timestampColumn: options.timestampColumn,
                venueIds: options.venueIds,
            },
        });
        if (!response.ok) {
            const errorData = response.data;
            throw new Error(`[VenueOS Analytics] Failed to fetch table data: ${errorData?.message || errorData?.error || response.status}`);
        }
        return {
            success: response.data.success,
            data: response.data.data,
            count: response.data.count,
            limit: response.data.limit,
            offset: response.data.offset,
        };
    }
    /**
     * Clean up the client
     */
    destroy() {
        if (this._messageHandler) {
            window.removeEventListener('message', this._messageHandler);
            this._messageHandler = null;
        }
        this._pendingTokenRequests.forEach((pending) => {
            pending.reject(new Error('[VenueOS Analytics] Client destroyed'));
        });
        this._pendingTokenRequests.clear();
    }
    /**
     * Set up message listener for analytics-specific messages
     */
    _setupMessageListener() {
        this._messageHandler = (event) => {
            const data = event.data;
            if (!isAnalyticsMessage(data)) {
                return;
            }
            if (isAnalyticsTokenResponseMessage(data)) {
                const pending = this._pendingTokenRequests.get(data.requestId);
                if (pending) {
                    if (data.success && data.credentials) {
                        pending.resolve(data.credentials);
                    }
                    else {
                        pending.reject(new Error(data.error || 'Failed to get Snowflake token'));
                    }
                }
            }
        };
        window.addEventListener('message', this._messageHandler);
    }
}
